import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Checkbox } from "@arcadeai/design-system/components/ui/atoms/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@arcadeai/design-system/components/ui/atoms/dropdown-menu";
import { Filter } from "lucide-react";
import type { AuditLogFilterOptions } from "@/app/audit-logs/types";
import type { AuditLogsSearch } from "@/routes/_authenticated/orgs/$organizationId/projects/$projectId/audit-logs";
import { DATE_RANGE_OPTIONS } from "../utils/date-range";
import {
  formatAuditAction,
  formatAuditSource,
  formatResourceType,
} from "../utils/enum-helpers";

type AuditLogFiltersProps = {
  filters: AuditLogsSearch;
  filterOptions?: AuditLogFilterOptions;
  onFilterChange: (updates: Partial<AuditLogsSearch>) => void;
};

function toggleValue<T>(current: T[], value: T): T[] {
  return current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];
}

/**
 * Merge currently selected values into the API-returned options so that
 * values which disappear from the API response (e.g. after a date-range or
 * project change) remain visible in the dropdown and can be deselected.
 */
function mergeWithSelected<T>(available: T[], selected: T[]): T[] {
  const merged = [...available];
  for (const value of selected) {
    if (!merged.includes(value)) {
      merged.push(value);
    }
  }
  return merged;
}

export const AuditLogFilters = ({
  filters,
  filterOptions,
  onFilterChange,
}: AuditLogFiltersProps) => {
  const availableActions = mergeWithSelected(
    filterOptions?.actions ?? [],
    filters.actions
  );
  const availableSources = mergeWithSelected(
    filterOptions?.sources ?? [],
    filters.sources
  );
  const availableResourceTypes = mergeWithSelected(
    filterOptions?.resource_types ?? [],
    filters.resourceTypes
  );

  const activeFilterCount =
    filters.actions.length +
    filters.sources.length +
    filters.resourceTypes.length +
    (filters.dateRange === "all" ? 0 : 1);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button className="h-9 border-dashed" size="sm" variant="outline" />
        }
      >
        <Filter className="mr-2 size-4" />
        Filters
        {activeFilterCount > 0 && (
          <Badge
            className="ml-2 rounded-sm px-1 font-normal"
            variant="secondary"
          >
            {activeFilterCount}
          </Badge>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[180px]">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            Date Range
            {filters.dateRange !== "all" && (
              <Badge
                className="ml-auto rounded-sm px-1 font-normal"
                variant="secondary"
              >
                1
              </Badge>
            )}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup
                onValueChange={(value) =>
                  onFilterChange({
                    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                    dateRange: value as AuditLogsSearch["dateRange"],
                  })
                }
                value={filters.dateRange}
              >
                {DATE_RANGE_OPTIONS.map((option) => (
                  <DropdownMenuRadioItem
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            Action
            {filters.actions.length > 0 && (
              <Badge
                className="ml-auto rounded-sm px-1 font-normal"
                variant="secondary"
              >
                {filters.actions.length}
              </Badge>
            )}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {availableActions.map((action) => {
                const isChecked = filters.actions.includes(action);
                return (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    key={action}
                    onClick={() =>
                      onFilterChange({
                        actions: toggleValue(filters.actions, action),
                      })
                    }
                    onSelect={(e) => e.preventDefault()}
                  >
                    <div className="flex items-center space-x-2">
                      <Checkbox checked={isChecked} />
                      <span>{formatAuditAction(action)}</span>
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            Source
            {filters.sources.length > 0 && (
              <Badge
                className="ml-auto rounded-sm px-1 font-normal"
                variant="secondary"
              >
                {filters.sources.length}
              </Badge>
            )}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {availableSources.map((source) => {
                const isChecked = filters.sources.includes(source);
                return (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    key={source}
                    onClick={() =>
                      onFilterChange({
                        sources: toggleValue(filters.sources, source),
                      })
                    }
                    onSelect={(e) => e.preventDefault()}
                  >
                    <div className="flex items-center space-x-2">
                      <Checkbox checked={isChecked} />
                      <span>{formatAuditSource(source)}</span>
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            Resource Type
            {filters.resourceTypes.length > 0 && (
              <Badge
                className="ml-auto rounded-sm px-1 font-normal"
                variant="secondary"
              >
                {filters.resourceTypes.length}
              </Badge>
            )}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
              {availableResourceTypes.map((resourceType) => {
                const isChecked = filters.resourceTypes.includes(resourceType);
                return (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    key={resourceType}
                    onClick={() =>
                      onFilterChange({
                        resourceTypes: toggleValue(
                          filters.resourceTypes,
                          resourceType
                        ),
                      })
                    }
                    onSelect={(e) => e.preventDefault()}
                  >
                    <div className="flex items-center space-x-2">
                      <Checkbox checked={isChecked} />
                      <span>{formatResourceType(resourceType)}</span>
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        {activeFilterCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="justify-center"
              onClick={() =>
                onFilterChange({
                  dateRange: "all",
                  actions: [],
                  sources: [],
                  resourceTypes: [],
                })
              }
            >
              Clear filters
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
